import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { BlogPost } from "@/lib/blog-types";

function parseTags(p: BlogPost): BlogPost {
  return { ...p, tags: Array.isArray(p.tags) ? p.tags : JSON.parse(p.tags as unknown as string) };
}

export function useBlogPosts() {
  return useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("published", true)
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as BlogPost[]).map(parseTags);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllBlogPosts() {
  return useQuery({
    queryKey: ["blog-posts-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as BlogPost[]).map(parseTags);
    },
  });
}

export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return parseTags(data as unknown as BlogPost);
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateBlogPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (post: {
      title: string;
      slug: string;
      excerpt: string;
      content: string;
      tags: string[];
      published: boolean;
      cover_image_url?: string | null;
      published_at?: string | null;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("blog_posts")
        .insert({
          ...post,
          tags: post.tags as unknown as string[],
          author_user_id: user.id,
          author_name: "Reqqy Team",
          published_at: post.published_at ?? (post.published ? new Date().toISOString() : null),
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
      queryClient.invalidateQueries({ queryKey: ["blog-posts-all"] });
    },
  });
}

export function useUpdateBlogPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id: string;
      title: string;
      slug: string;
      excerpt: string;
      content: string;
      tags: string[];
      published: boolean;
      cover_image_url?: string | null;
      published_at?: string | null;
    }) => {
      const { id, ...post } = params;
      const { data, error } = await supabase
        .from("blog_posts")
        .update({
          ...post,
          tags: post.tags as unknown as string[],
          published_at: post.published_at ?? (post.published ? new Date().toISOString() : null),
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
      queryClient.invalidateQueries({ queryKey: ["blog-posts-all"] });
      queryClient.invalidateQueries({ queryKey: ["blog-post"] });
    },
  });
}
